import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, Download, FileText, X, Fingerprint, Copy, ExternalLink,
  Loader2, Layers, Database, Lock, Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { EVIDENCE } from "@/constants/testIds";
import { getEvidence, attachEvidence, downloadEvidence } from "@/lib/evidence";
import { EvidenceShareDialog } from "@/components/evidence/EvidenceShareDialog";

const SEMA_PRIMARY = "#1F3A93";

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-3 py-1.5 text-xs">
      <div className="font-mono uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="min-w-0 break-words text-foreground/90">{children}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-6">
      <h3
        className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em]"
        style={{ color: SEMA_PRIMARY }}
      >
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: SEMA_PRIMARY }} />
        {title}
      </h3>
      <div className="rounded-lg border border-border/60 bg-background/40 p-3">{children}</div>
    </section>
  );
}

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  } catch {
    toast.error("Could not copy");
  }
}

/**
 * `EvidencePanel` — renders a signed evidence bundle for an answer.
 *
 * Modes:
 *  - If `answerId` is already known, we fetch the persisted bundle.
 *  - Else if a `context` is provided, we call `/api/evidence/attach` on open
 *    to (idempotently) generate + fetch the pack for that answer.
 */
export function EvidencePanel({ open, onOpenChange, answerId, context }) {
  const [loading, setLoading] = useState(false);
  const [pack, setPack] = useState(null);
  const [effectiveId, setEffectiveId] = useState(answerId || null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let doc = null;
        if (answerId) {
          doc = await getEvidence(answerId);
          if (!cancelled) setEffectiveId(answerId);
        } else if (context) {
          doc = await attachEvidence(context);
          if (!cancelled) setEffectiveId(doc?.answer_id || null);
        }
        if (!cancelled) setPack(doc);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load evidence.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, answerId, context]);

  const bundle = pack?.bundle || {};
  const lineage = bundle.lineage || {};
  const rbac = bundle.rbac || {};
  const health = bundle.table_health || [];

  const onExport = async (fmt) => {
    if (!effectiveId) return;
    try {
      await downloadEvidence(effectiveId, fmt);
      toast.success(`Downloaded evidence.${fmt}`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto p-0 sm:max-w-[600px]"
        data-testid={EVIDENCE.panel}
      >
        <div
          className="sticky top-0 z-10 flex items-center justify-between border-b px-5 py-4 backdrop-blur"
          style={{ background: "rgba(31,58,147,0.06)", borderColor: "rgba(31,58,147,0.25)" }}
        >
          <SheetHeader className="p-0 text-left">
            <SheetTitle className="flex items-center gap-2 font-mono text-sm">
              <ShieldCheck className="h-4 w-4" style={{ color: SEMA_PRIMARY }} />
              Evidence Pack
            </SheetTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Signed audit bundle · Ed25519
            </p>
          </SheetHeader>
          <button
            data-testid={EVIDENCE.panelClose}
            onClick={() => onOpenChange(false)}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-muted/60"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 px-5 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading evidence…
          </div>
        )}

        {!loading && pack && (
          <div className="px-5 pb-8 pt-5">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid={EVIDENCE.exportPdf}
                onClick={() => onExport("pdf")}
                className="h-9 gap-1.5 rounded-full text-xs text-white hover:opacity-95"
                style={{ background: SEMA_PRIMARY }}
              >
                <FileText className="h-3.5 w-3.5" /> Export PDF
              </Button>
              <Button
                data-testid={EVIDENCE.exportJson}
                onClick={() => onExport("json")}
                variant="outline"
                className="h-9 gap-1.5 rounded-full border-[color:rgba(31,58,147,0.4)] text-xs"
                style={{ color: SEMA_PRIMARY }}
              >
                <Download className="h-3.5 w-3.5" /> Export JSON
              </Button>
              <Button
                data-testid={EVIDENCE.shareBtn}
                onClick={() => setShareOpen(true)}
                variant="outline"
                className="h-9 gap-1.5 rounded-full border-[color:rgba(31,58,147,0.4)] text-xs"
                style={{ color: SEMA_PRIMARY }}
              >
                <Share2 className="h-3.5 w-3.5" /> Share
              </Button>
              <Badge variant="outline" className="rounded-full font-mono text-[10px] uppercase tracking-[0.14em]">
                <Fingerprint className="mr-1 h-3 w-3" /> {pack.signing_kid}
              </Badge>
            </div>

            <Section title="Overview">
              <Row label="Answer ID">
                <span
                  data-testid={EVIDENCE.answerId}
                  className="font-mono text-[11px] break-all text-foreground/80"
                >
                  {effectiveId}
                </span>
              </Row>
              <Row label="Source">{bundle.source_kind || "chat"}</Row>
              <Row label="Generated at">{bundle.generated_at}</Row>
              <Row label="Row count">{bundle.row_count ?? "—"}</Row>
              <Row label="Graph version">
                <span data-testid={EVIDENCE.graphVersion} className="font-mono text-[11px]">
                  {bundle.graph_version}
                </span>
              </Row>
            </Section>

            <Section title="Question">
              <p data-testid={EVIDENCE.question} className="text-sm text-foreground/90">
                {bundle.question || "(no question captured)"}
              </p>
            </Section>

            <Section title="Explanation">
              <p data-testid={EVIDENCE.explanation} className="text-sm leading-relaxed text-foreground/90">
                {bundle.explanation}
              </p>
            </Section>

            <Section title="Generated SQL">
              <div className="flex items-start justify-between gap-2">
                <pre
                  data-testid={EVIDENCE.sql}
                  className="max-h-56 flex-1 overflow-auto rounded bg-background/60 p-3 font-mono text-[11px] text-foreground/80"
                >
                  {bundle.sql || "(no SQL)"}
                </pre>
                {bundle.sql && (
                  <button
                    onClick={() => copyToClipboard(bundle.sql)}
                    className="rounded-md border border-border/60 p-1.5 text-muted-foreground hover:text-foreground"
                    aria-label="Copy SQL"
                    title="Copy SQL"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </Section>

            <Section title="Tables & lineage">
              {(lineage.tables || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No tables recorded.</p>
              ) : (
                <ul className="space-y-2">
                  {(lineage.tables || []).map((t) => (
                    <li
                      key={t.table_fqn}
                      data-testid={EVIDENCE.lineageTable}
                      className="rounded-md border border-border/40 p-2"
                    >
                      <div className="flex items-center gap-2 text-xs">
                        <Database className="h-3.5 w-3.5" style={{ color: SEMA_PRIMARY }} />
                        <span className="font-mono">{t.table_fqn}</span>
                        <span className="ml-auto text-[11px] text-muted-foreground">
                          {t.row_count ?? "?"} rows
                        </span>
                      </div>
                      <div className="mt-1 pl-5 text-[11px] text-muted-foreground">
                        {(t.columns || []).slice(0, 12).map((c) => c.name).join(", ") || "—"}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {(lineage.join_edges || []).length > 0 && (
                <div className="mt-3 border-t border-border/40 pt-3">
                  <div className="mb-1 flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
                    <Layers className="h-3 w-3" /> join lineage
                  </div>
                  <ul className="space-y-0.5">
                    {(lineage.join_edges || []).map((e, i) => (
                      <li key={i} className="text-[11px] font-mono text-foreground/70">
                        {e.from_fqn}.{e.from_column} → {e.to_fqn}.{e.to_column}{" "}
                        <span className="text-muted-foreground">
                          [{e.kind || "joins_on"} · {e.status || "suggested"}]
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>

            <Section title="RBAC context">
              <Row label="User">{rbac.user_email || rbac.user_id || "system"}</Row>
              <Row label="Role">
                <span data-testid={EVIDENCE.rbacRole}>{rbac.role}</span>
              </Row>
              <Row label="Masked columns">
                {(rbac.masked_columns || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {rbac.masked_columns.map((c) => (
                      <Badge key={c} variant="outline" className="rounded-full text-[10px]">
                        <Lock className="mr-1 h-2.5 w-2.5" />
                        {c}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">none</span>
                )}
              </Row>
            </Section>

            {health.length > 0 && (
              <Section title="Table health at answer time">
                <ul className="space-y-1">
                  {health.map((h) => (
                    <li key={h.table_fqn} className="flex items-center gap-2 text-xs">
                      <span
                        className={`inline-block h-1.5 w-1.5 rounded-full ${
                          h.status === "ok" ? "bg-emerald-500" : "bg-amber-500"
                        }`}
                      />
                      <span className="font-mono text-[11px]">{h.table_fqn}</span>
                      <span className="ml-auto text-[11px] text-muted-foreground">
                        {h.row_count} rows · {h.status}
                      </span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <Section title="Signature">
              <Row label="Key ID">{pack.signing_kid}</Row>
              <Row label="Algorithm">Ed25519</Row>
              <Row label="Signature">
                <span
                  data-testid={EVIDENCE.signature}
                  className="font-mono text-[10px] break-all text-foreground/70"
                >
                  {pack.signature}
                </span>
              </Row>
              <div className="mt-2 text-[10px] text-muted-foreground">
                Signed over the canonical JSON of the bundle. Verify with the workspace
                public key returned by <span className="font-mono">GET /api/evidence/_/pubkey</span>.
              </div>
            </Section>
          </div>
        )}

        {!loading && !pack && (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">
            <ExternalLink className="mx-auto mb-2 h-6 w-6 opacity-60" />
            No evidence pack found for this answer.
          </div>
        )}
      </SheetContent>
      <EvidenceShareDialog
        answerId={effectiveId}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
    </Sheet>
  );
}

/**
 * Small inline trigger — a "Show / Attach evidence" button appropriate for
 * chat answers and report rows. Handles state locally.
 */
export function EvidenceTrigger({ answerId, context, size = "sm", variant = "outline", label }) {
  const [open, setOpen] = useState(false);
  const hasPack = !!answerId;
  const testId = hasPack ? EVIDENCE.showBtn : EVIDENCE.attachBtn;
  return (
    <>
      <Button
        data-testid={testId}
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className="h-7 gap-1.5 rounded-full text-[11px]"
        style={{
          borderColor: hasPack ? "rgba(31,58,147,0.4)" : undefined,
          color: hasPack ? SEMA_PRIMARY : undefined,
        }}
      >
        <ShieldCheck className="h-3.5 w-3.5" />
        {label || (hasPack ? "Show evidence" : "Attach evidence")}
      </Button>
      <EvidencePanel
        open={open}
        onOpenChange={setOpen}
        answerId={answerId || null}
        context={hasPack ? null : context}
      />
    </>
  );
}
